import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, Mail, Shield, Loader2 } from "lucide-react";
import axios from "axios";
import useSeo from "../hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmployeeLoginPage() {
  useSeo({
    title: "Medarbejder-login · EMA Kurer",
    description: "Internt login for EMA Kurer medarbejdere — operations, finance og support.",
    path: "/employee/login",
  });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Udfyld email og password");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/employee/auth/login`, { email, password });
      const { token, employee } = r.data;
      localStorage.setItem("ema_employee_token", token);
      localStorage.setItem("ema_employee", JSON.stringify(employee));
      toast.success(`Velkommen, ${employee.name || employee.email}`);
      if (employee.must_change_password) {
        navigate("/employee/skift-password");
      } else {
        navigate("/employee");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login fejlede";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark text-white flex items-center justify-center px-5" data-testid="employee-login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 bg-brand-red/10 border border-brand-red/30 px-3 py-1.5 mb-4">
            <Shield size={14} className="text-brand-red"/>
            <span className="text-[10px] font-bold tracking-widest uppercase text-brand-red">Internt login</span>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Medarbejder-portal</h1>
          <p className="text-white/60 mt-3 text-sm">Operations · Finance · Support</p>
        </div>

        <form onSubmit={onSubmit} className="bg-white/[0.04] border border-white/10 p-7 space-y-5" data-testid="employee-login-form">
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-white/60 mb-2">
              <Mail size={12} className="inline mr-1.5"/> Email
            </label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="employee-login-email"
              className="w-full bg-brand-dark border border-white/15 px-4 py-3 text-white focus:border-brand-red focus:outline-none"
              placeholder="medarbejder@emakurer.dk"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-white/60 mb-2">
              <Lock size={12} className="inline mr-1.5"/> Password
            </label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="employee-login-password"
              className="w-full bg-brand-dark border border-white/15 px-4 py-3 text-white focus:border-brand-red focus:outline-none"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit" disabled={submitting} data-testid="employee-login-submit"
            className="w-full bg-brand-red hover:bg-brand-red/90 text-white py-3 font-bold tracking-wider uppercase text-sm transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="animate-spin" size={16}/> : <>Log ind <ArrowRight size={16}/></>}
          </button>
          <div className="text-center text-xs text-white/40 pt-3 border-t border-white/10">
            Modtag invite? Brug det midlertidige password fra din admin. <br/>
            Ved problemer: <a href="mailto:kontakt@emakurer.dk" className="text-brand-red hover:underline">kontakt@emakurer.dk</a>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link to="/portal" className="text-xs text-white/40 hover:text-white">← Tilbage til login-oversigt</Link>
        </div>
      </div>
    </div>
  );
}
